import { supabase } from "@/integrations/supabase/client";
import { ApiService } from "@/services/api/ApiService";

import type { EmailLog, EmailStats, EmailFilters } from "@/types/email-logs";

export class EmailLoggingService {
  /**
   * Get all email logs with optional filtering
   */
  static async getEmailLogs(
    filters?: EmailFilters,
    limit: number = 50,
    offset: number = 0,
  ): Promise<{ data: EmailLog[]; count: number }> {
    let query = supabase
      .from("email_logs")
      .select("*", { count: "exact" })
      .order("created_at", { ascending: false })
      .range(offset, offset + limit - 1);

    // Apply filters
    if (filters?.email_type) {
      query = query.eq("email_type", filters.email_type);
    }

    if (filters?.status) {
      query = query.eq("status", filters.status);
    }

    if (filters?.agent_id) {
      query = query.eq("agent_id", filters.agent_id);
    }

    if (filters?.date_from) {
      query = query.gte("created_at", filters.date_from);
    }

    if (filters?.date_to) {
      query = query.lte("created_at", filters.date_to);
    }

    if (filters?.search) {
      query = query.or(
        `subject.ilike.%${filters.search}%,recipients.cs.{${filters.search}}`,
      );
    }

    const { data, error, count } = await query;

    if (error) throw error;

    return { data: data || [], count: count || 0 };
  }

  /**
   * Get a single email log by ID
   */
  static async getEmailLog(id: string): Promise<EmailLog> {
    const { data, error } = await supabase
      .from("email_logs")
      .select("*")
      .eq("id", id)
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Get email statistics
   */
  static async getEmailStats(filters?: EmailFilters): Promise<EmailStats> {
    let query = supabase
      .from("email_logs")
      .select("status", { count: "exact" });

    if (filters?.email_type) {
      query = query.eq("email_type", filters.email_type);
    }

    if (filters?.agent_id) {
      query = query.eq("agent_id", filters.agent_id);
    }

    if (filters?.date_from) {
      query = query.gte("created_at", filters.date_from);
    }

    if (filters?.date_to) {
      query = query.lte("created_at", filters.date_to);
    }

    const { data, error, count } = await query;

    if (error) throw error;

    const total = count || 0;
    const sent = data?.filter((log) => log.status === "sent").length || 0;
    const failed = data?.filter((log) => log.status === "failed").length || 0;
    const pending =
      data?.filter(
        (log) => log.status === "pending" || log.status === "retrying",
      ).length || 0;
    const successRate = total > 0 ? Math.round((sent / total) * 100) : 0;

    return {
      total,
      sent,
      failed,
      pending,
      successRate,
    };
  }

  /**
   * Resend a failed email
   */
  static async resendEmail(emailLogId: string): Promise<void> {
    await ApiService.invoke({
      resource: "email-logs",
      action: "resend",
      id: emailLogId,
    });
  }

  /**
   * Export email logs to CSV
   */
  static async exportToCSV(filters?: EmailFilters): Promise<string> {
    const { data } = await this.getEmailLogs(filters, 10000, 0);

    const headers = [
      "Date",
      "Type",
      "Subject",
      "Recipients",
      "Status",
      "Provider",
      "Error",
    ];
    const rows = data.map((log) => [
      new Date(log.created_at).toLocaleString(),
      log.email_type,
      log.subject,
      log.recipients.join("; "),
      log.status,
      log.delivery_provider,
      log.error_message || "",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    return csvContent;
  }
}
